




















































































import { defineComponent, PropType, reactive, Ref, ref, watch } from '@vue/composition-api';
import OlbInput from '../elements/input/Input.vue';
import OlbRangeSlider from '../elements/input/RangeSlider.vue';
import OlbButton from '../../button';
import rules from '../rules';
import { ValidationObserver } from 'vee-validate';
import { clamp, result } from 'lodash';

rules();

interface CalcInputSettings {
	label: string;
	step: string;
	min: string;
	max: string;
	value: string;
	type: string;
}

interface Settings {
	capital: {
		label: string;
		required: string;
		error: string;
	};
	rent: CalcInputSettings;
	monthlyCapital: CalcInputSettings;
	result: {
		title: string;
		success: string;
		error: string;
	};
	interest: string;
	clearance: string;
}

export default defineComponent({
	components: {
		OlbInput,
		OlbRangeSlider,
		OlbButton,
		ValidationObserver,
	},
	props: {
		settings: {
			type: Object as PropType<Settings>,
			required: true,
		},
	},
	setup(props) {
		const observer = ref();
		const mode = ref('passive');
		const isValid = ref(false);

		const values = reactive({
			rent: props.settings.rent.value,
			monthlyCapital: props.settings.monthlyCapital.value,
			capital: '',
		});

		const resultText = ref<string | null>(null);

		function roundIncrement(number: number, increment: number) {
			return Math.ceil(number / increment) * increment;
		}

		const clampValue = (passedValue: string, key: 'rent' | 'monthlyCapital') => {
			const value = parseInt(passedValue);
			const min = parseInt(props.settings[key].min);
			const max = parseInt(props.settings[key].max);

			if (isNaN(value)) {
				values[key] = min.toString();
				return;
			}

			if (value < min || value > max) {
				values[key] = clamp(value, min, max).toString();
				return;
			}

			values[key] = roundIncrement(value, parseInt(props.settings[key].step)).toString();
		};

		const disallowInvalidInput = (value: string, old: string, type: 'rent' | 'monthlyCapital' | 'capital') => {
			if (value === '') return;
			if (!/^[0-9]+$/.test(values[type])) {
				values[type] = old;
			}
		};

		const formatLocale = (stringValue: string) =>
			parseInt(stringValue).toLocaleString('de-DE', { maximumFractionDigits: 2, minimumFractionDigits: 2 });

		const handleSubmit = async (e: MouseEvent) => {
			e.preventDefault();
			mode.value = 'aggressive';
			isValid.value = await observer.value.validate();

			if (!isValid) {
				resultText.value = null;
				return;
			}

			const cost =
				(((parseInt(values.rent) + parseInt(values.monthlyCapital)) * 12) /
					(parseFloat(props.settings.interest) + parseFloat(props.settings.clearance))) *
					100 +
				parseInt(values.capital);

			resultText.value = props.settings.result.success
				.replace('[[=capitalVal]]', formatLocale(values.capital))
				.replace('[[=rentVal]]', formatLocale(values.rent))
				.replace('[[=monthlycapitalVal]]', formatLocale(values.monthlyCapital))
				.replace('[[=cost]]', cost.toLocaleString('de-DE', { maximumFractionDigits: 0 }))
				.replace(
					'[[=interest]]',
					parseFloat(props.settings.interest).toLocaleString('de-DE', { maximumFractionDigits: 1 }),
				)
				.replace(
					'[[=clearance]]',
					parseFloat(props.settings.clearance).toLocaleString('de-DE', { maximumFractionDigits: 1 }),
				);
		};

		watch(
			() => values.rent,
			(current, old) => disallowInvalidInput(current, old, 'rent'),
		);

		watch(
			() => values.monthlyCapital,
			(current, old) => disallowInvalidInput(current, old, 'monthlyCapital'),
		);

		watch(
			() => values.capital,
			(current, old) => disallowInvalidInput(current, old, 'capital'),
		);

		return {
			values,
			resultText,
			mode,
			observer,
			isValid,
			clampValue,
			handleSubmit,
		};
	},
});
